import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BookOpen, Loader2 } from "lucide-react";
import { DX } from "@/constants/testIds";
import { INTERNAL_DIAGNOSIS_KEYS } from "@/lib/diagnosisApi";

function NoteList({ title, items }) {
  if (!items?.length) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h4>
      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
        {items.map((item, i) => (
          <li key={`${title}-${i}`}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

function Field({ label, children }) {
  if (children == null || children === "" || (Array.isArray(children) && !children.length)) {
    return null;
  }
  return (
    <div className="space-y-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function HierarchySection({ diagnosis }) {
  const chapter = diagnosis.chapter;
  const block = diagnosis.block;
  if (!chapter && !block && !diagnosis.categoryCode && !diagnosis.hierarchyLevel) return null;

  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Hierarchy / Category
      </h4>
      <dl className="grid gap-3 sm:grid-cols-2">
        <Field label="Hierarchy level">
          {diagnosis.hierarchyLevel && (
            <Badge variant="secondary" className="capitalize">
              {diagnosis.hierarchyLevel}
            </Badge>
          )}
        </Field>
        {chapter && (
          <Field label="Chapter">
            {chapter.number && <span className="mr-1 font-medium">Ch. {chapter.number}</span>}
            {chapter.codeRange && (
              <span className="font-fhir text-slate-600">({chapter.codeRange}) </span>
            )}
            {chapter.title}
          </Field>
        )}
        {block && (
          <Field label="Block">
            <span className="font-fhir text-slate-600">{block.codeRange}</span>
            {block.title && <span> — {block.title}</span>}
          </Field>
        )}
        <Field label="Category code">
          {diagnosis.categoryCode && (
            <span className="font-fhir">{diagnosis.categoryCode}</span>
          )}
        </Field>
      </dl>
    </section>
  );
}

function AncestorsSection({ diagnosis }) {
  const ancestors = [
    ...(diagnosis.ancestorCodes || []),
  ];
  if (diagnosis.parentCode && !ancestors.includes(diagnosis.parentCode)) {
    ancestors.unshift(diagnosis.parentCode);
  }
  if (!ancestors.length) return null;

  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Parent / Ancestors
      </h4>
      <div className="flex flex-wrap gap-2">
        {ancestors.map((code) => (
          <Badge key={code} variant="outline" className="font-fhir">
            {code}
          </Badge>
        ))}
      </div>
    </section>
  );
}

function SevenChrSection({ sevenChrDef, sevenChrRequired }) {
  if (!sevenChrDef && !sevenChrRequired) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        7th-Character Information
      </h4>
      {sevenChrRequired && (
        <p className="text-sm text-amber-800">
          A 7th character extension is required for this code category.
        </p>
      )}
      {sevenChrDef?.extensions?.length > 0 && (
        <ul className="space-y-2">
          {sevenChrDef.extensions.map((ext) => (
            <li key={ext.char} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
              <span className="font-fhir font-semibold text-teal-800">{ext.char}</span>
              <span className="text-slate-600"> — {ext.description}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LateralitySection({ diagnosis }) {
  const { lateralityRequired, lateralityInCode, extensionPlaceholder } = diagnosis;
  if (!lateralityRequired && !lateralityInCode && !extensionPlaceholder) return null;

  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Laterality Information
      </h4>
      <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
        {lateralityInCode && <li>Laterality is encoded in the code description.</li>}
        {lateralityRequired && <li>A laterality 7th character is required.</li>}
        {extensionPlaceholder && <li>Code is a placeholder requiring a 7th-character extension.</li>}
      </ul>
    </section>
  );
}

function SourceSection({ source, versionLabel, versionYear }) {
  if (!source && !versionLabel) return null;
  return (
    <section className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Source / Version
      </h4>
      <dl className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2">
        <Field label="Version">{versionLabel || (versionYear ? `FY${versionYear}` : null)}</Field>
        <Field label="Publisher">{source?.publisher}</Field>
        <Field label="Release">{source?.releaseName}</Field>
        <Field label="Release date">{source?.releaseDate}</Field>
      </dl>
    </section>
  );
}

export default function DiagnosisDetailDialog({ open, diagnosis, loading, onClose }) {
  const code = diagnosis?.code || diagnosis?.codeNormalized;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={DX.detailDialog}
        className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <DialogHeader className="space-y-3 border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-start gap-3 pr-6">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-teal-500/15 ring-1 ring-teal-400/30">
              <BookOpen className="h-5 w-5 text-teal-700" />
            </div>
            <div className="min-w-0 space-y-1">
              <DialogTitle className="font-fhir text-xl tracking-tight">
                {code || "ICD-10-CM Code"}
              </DialogTitle>
              <DialogDescription className="text-left text-sm text-slate-600">
                {diagnosis?.description || "Terminology reference — not a patient diagnosis."}
              </DialogDescription>
            </div>
          </div>
          {diagnosis && (
            <div className="flex flex-wrap gap-2">
              {diagnosis.isBillable ? (
                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Billable</Badge>
              ) : (
                <Badge variant="secondary">Non-billable / Header</Badge>
              )}
              {diagnosis.active !== false ? (
                <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">Active</Badge>
              ) : (
                <Badge variant="secondary">Inactive</Badge>
              )}
              {diagnosis.isHeader && (
                <Badge variant="outline">Header code</Badge>
              )}
            </div>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-10rem)] px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading code details…
            </div>
          ) : !diagnosis ? (
            <p className="py-16 text-center text-sm text-slate-500">No diagnosis selected.</p>
          ) : (
            <div className="space-y-6 pb-4">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                Reference data only — viewing this code does not create or assign a patient diagnosis.
              </div>

              <dl className="grid gap-4 sm:grid-cols-2">
                <Field label="Official description">{diagnosis.description}</Field>
                <Field label="Short description">{diagnosis.shortDescription}</Field>
              </dl>

              <Separator />

              <HierarchySection diagnosis={diagnosis} />
              <AncestorsSection diagnosis={diagnosis} />

              <NoteList title="Inclusion terms" items={diagnosis.inclusionTerms} />
              <NoteList title="Excludes1" items={diagnosis.excludes1} />
              <NoteList title="Excludes2" items={diagnosis.excludes2} />
              <NoteList title="Code first" items={diagnosis.codeFirst} />
              <NoteList title="Use additional code" items={diagnosis.useAdditionalCode} />
              <NoteList title="Code also" items={diagnosis.codeAlso} />
              <NoteList title="Notes" items={diagnosis.notes} />
              <NoteList title="Includes notes" items={diagnosis.includesNotes} />

              <LateralitySection diagnosis={diagnosis} />
              <SevenChrSection
                sevenChrDef={diagnosis.sevenChrDef}
                sevenChrRequired={diagnosis.sevenChrRequired}
              />
              <SourceSection
                source={diagnosis.source}
                versionLabel={diagnosis.versionLabel}
                versionYear={diagnosis.versionYear}
              />

              {/* Guard: internal keys must never appear */}
              {diagnosis &&
                Object.keys(diagnosis).some((k) => INTERNAL_DIAGNOSIS_KEYS.has(k)) && (
                  <p className="text-xs text-rose-600">
                    Warning: internal search fields detected in API response.
                  </p>
                )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
